import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useMutation } from '@apollo/client'
import { Delete } from '@factorialco/f0-react/icons/app'
import {
  MeetingsMeetingCanDeleteDocument,
  OneOnOneGetOrCreateMeetingFromTemplateDocument,
  OneOnOnesScheduledMeetingItemFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { formatDateOnlyToString, formatTimeToString } from 'modules/meetings/lib/utils/dates'

interface UseMeetingNavigationItemsParams {
  employeeId: string
  refetch: () => void
}

export const useMeetingNavigationItems = ({
  employeeId,
  refetch,
}: UseMeetingNavigationItemsParams) => {
  const [openDelete, setOpenDelete] = useState(false)
  const [meetingToDelete, setMeetingToDelete] =
    useState<OneOnOnesScheduledMeetingItemFragment | null>(null)

  const {
    access: { locale, timeFormat },
    employee: currentEmployee,
  } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { meetingId } = useParams<{ meetingId: string }>()

  const { data: canDeleteData } = useQuery(MeetingsMeetingCanDeleteDocument, {
    variables: { employeeId: String(employeeId) },
    skip: !employeeId,
  })

  const [getOrCreateMeeting] = useMutation(OneOnOneGetOrCreateMeetingFromTemplateDocument)

  const canDelete =
    canDeleteData?.employees?.employeesConnection?.nodes?.some(
      (employee) => employee.id === String(employeeId)
    ) ?? false

  const handleMeetingClick = (meeting: OneOnOnesScheduledMeetingItemFragment) => {
    if (meeting.id) {
      navigate(
        navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
          employeeId: String(employeeId),
          meetingId: String(meeting.id),
        })
      )
    } else if (meeting.template?.id && meeting.startTime) {
      // Create meeting from template and then redirect
      getOrCreateMeeting({
        variables: {
          templateId: String(meeting.template.id),
          timestamp: meeting.startTime,
        },
      }).then((result) => {
        if (result.data?.meetingsMutations?.getOrCreateFromTemplateMeetingsMeeting?.meeting?.id) {
          navigate(
            navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
              employeeId: String(employeeId),
              meetingId: String(
                result.data.meetingsMutations.getOrCreateFromTemplateMeetingsMeeting.meeting.id
              ),
            })
          )
          refetch()
        }
      })
    }
  }

  const handleDeleteMeetingClick = (meeting: OneOnOnesScheduledMeetingItemFragment) => {
    setMeetingToDelete(meeting)
    setOpenDelete(true)
  }

  const handleCloseDelete = () => {
    setOpenDelete(false)
    setMeetingToDelete(null)
  }

  const createMeetingItems = (meetings: OneOnOnesScheduledMeetingItemFragment[]) => {
    const groupedMeetings = meetings.reduce<
      Record<string, OneOnOnesScheduledMeetingItemFragment[]>
    >((acc, meeting) => {
      if (!meeting.date) return acc

      const dayKey = meeting.date

      if (!acc[dayKey]) {
        acc[dayKey] = []
      }

      acc[dayKey].push(meeting)
      return acc
    }, {})

    return Object.entries(groupedMeetings)
      .sort(([dayKeyA], [dayKeyB]) => moment(dayKeyB).diff(moment(dayKeyA)))
      .map(([dayKey, dayMeetings]) => {
        const sortedDayMeetings = dayMeetings.sort((a, b) => {
          if (!a.startTime || !b.startTime) return 0
          return moment(b.startTime).diff(moment(a.startTime))
        })

        if (sortedDayMeetings.length === 1 && sortedDayMeetings[0]) {
          const meeting = sortedDayMeetings[0]
          const isOrganizer = meeting.template?.organizer?.id === currentEmployee?.id

          return {
            id: meeting.id?.toString() ?? 'no-meeting-id',
            label: formatDateOnlyToString(dayKey, locale),
            onClick: () => handleMeetingClick(meeting),
            otherActions:
              canDelete || isOrganizer
                ? [
                    {
                      icon: Delete,
                      label: i18n.t('common.delete'),
                      onClick: () => handleDeleteMeetingClick(meeting),
                    },
                  ]
                : [],
          }
        }

        return {
          id: dayKey,
          label: formatDateOnlyToString(dayKey, locale),
          children: sortedDayMeetings.map((meeting) => {
            const timeToStart = formatTimeToString(meeting.startTime, locale, timeFormat)
            const timeToEnd = formatTimeToString(meeting.endTime, locale, timeFormat)
            const label = `${timeToStart} - ${timeToEnd}`

            const isOrganizer = meeting.template?.organizer?.id === currentEmployee?.id

            return {
              id: meeting.id?.toString() ?? 'no-meeting-id',
              label,
              onClick: () => handleMeetingClick(meeting),
              otherActions:
                canDelete || isOrganizer
                  ? [
                      {
                        icon: Delete,
                        label: i18n.t('common.delete'),
                        onClick: () => handleDeleteMeetingClick(meeting),
                      },
                    ]
                  : [],
            }
          }),
        }
      })
  }

  const createMeetingItemsByMonth = (meetings: OneOnOnesScheduledMeetingItemFragment[]) => {
    const meetingsByMonth = meetings.reduce<
      Record<string, OneOnOnesScheduledMeetingItemFragment[]>
    >((acc, meeting) => {
      if (!meeting.date) return acc

      const monthKey = moment(meeting.date, 'YYYY-MM-DD').locale(locale).format('MM')

      if (!acc[monthKey]) {
        acc[monthKey] = []
      }

      acc[monthKey].push(meeting)
      return acc
    }, {})

    return Object.entries(meetingsByMonth)
      .map(([monthKey, monthMeetings]) => {
        const monthLabel = moment(monthKey, 'MM').locale(locale).format('MMMM')
        const dayItems = createMeetingItems(monthMeetings)

        return {
          id: `month-${monthKey}`,
          label: monthLabel,
          children: dayItems,
        }
      })
      .sort((a, b) => b.id.localeCompare(a.id, undefined, { numeric: true }))
  }

  return {
    createMeetingItems,
    createMeetingItemsByMonth,
    meetingId,
    openDelete,
    meetingToDelete,
    handleCloseDelete,
    refetch,
  }
}
